import { splitIntoSentences } from 'core/utils/sentenceSplitter';
import { findBestMatch } from 'string-similarity';

export const getSortedScores = (scores: number[]) => [...scores].sort((a, b) => b - a);

export const getSummarySentenceSequence = (
  {
    sentenceSequence,
    scores,
    sortedScores,
  }: { sentenceSequence: string[]; scores: number[]; sortedScores: number[] },
  extLengthGauge: number
) => {
  const scoreLimit =
    extLengthGauge <= sortedScores.length
      ? sortedScores[extLengthGauge - 1]
      : sortedScores[sortedScores.length - 1];
  return sentenceSequence.filter((sentenceId, index: number) => scores[index] >= scoreLimit);
};

export const getNumberOfSentences = (para: string) => {
  const sentences = splitIntoSentences(para)
    .filter((s) => s.type === 'Sentence')
    .map((s) => s.raw);
  return sentences.length;
};

export const getOutputKey = (summaryMode: string, absLengthGauge: unknown) => {
  if (summaryMode === 'ext') {
    return 'ext';
  }
  return `abs~${absLengthGauge}`;
};

const getUrlParameterByName = (name: string, url?: string) => {
  let currentUrl = url;
  let currentName = name;

  if (!url) currentUrl = window.location.href;
  currentName = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${currentName}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(`${currentUrl}`);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const getDefaultSummaryMode = (): 'abs' | 'ext' => {
  if (process.browser) {
    const mode = getUrlParameterByName('mode');
    if (mode && (mode === 'abs' || mode === 'ext')) {
      return mode;
    }
  }
  return 'abs';
};

export const filterOutputSummary = (summary: string): string => {
  // const optionalRegex = /(^|\s*)(CNN\.)/gim;
  // const requiredRegex = /(\s*)(Â(.)*|back\s*to\s*the\s*(page(s?)|site)\s*you\s*(came|were)\s*(from|to):?|Back to Mail Online home|To see the original post on CNN\.com, click here)(\s*\.?)/gim;
  let lastSentenceRemoved = false;
  let filteredSummary = '';
  const garbageStrings = [
    'back to the site pages you came were from',
    'Back to Mail Online home',
    'back to the original page',
    'The page you were from',
    'Click here to go to the site you came to the post',
    'CNN',
    'Back to the site you came in to the paper.',
    'The Daily Mail Online is on sale now for week',
    'To join the Daily Discussion on climate change, email us at: editorial@dailymail.co.uk',
    'says Amitai Etzioni, CTO of Veritiv.      To see the full interview with Amitav Agrawal, visitcom/soulmatestories.',
    'back to the pageYou came from: ',
    ', says John Boulden, CTO of security firm Symantec.',
    ', says Bouldan',
    ', Defterio says.   For confidential support call the National Suicide Prevention Lifeline at 1-800-273-8255.',
    'Back To the pageYou came to the site you camefrom.',
    'Back  to the Daily Mail home.',
    "The Daily Mail is a weekly, offbeat look at what's happening in the world of business",
    'For more information on the BBC iPlayer visit the BBC website',
  ];
  const sentences = splitIntoSentences(summary);
  sentences.forEach((sentence) => {
    if (sentence.type === 'Sentence') {
      if (!sentence.raw.includes('Â')) {
        const similarity = findBestMatch(sentence.raw, garbageStrings);
        if (similarity.bestMatch.rating < 0.7) {
          // console.log('[Keep Sentence] ', sentence.raw, similarity.bestMatch.rating);
          filteredSummary += sentence.raw;
          lastSentenceRemoved = false;
        } else {
          lastSentenceRemoved = true;
          // console.log('[Remove Sentence] ', sentence.raw, similarity.bestMatch.rating);
        }
      } else {
        lastSentenceRemoved = true;
        // console.log('[Remove Sentence] ', sentence.raw, 'Â');
      }
    } else if (!lastSentenceRemoved) {
      filteredSummary += sentence.raw;
    }
  });
  return filteredSummary;
};

/**
 * Checks if paragraph break is already present in the surrounding sentence.
 */
const noParaAhead = (summarySplittedArray: any, index: number) => {
  if (index <= 3 || index >= summarySplittedArray.length - 3) {
    return false;
  }
  for (let jIndex = index; jIndex <= index + 2; jIndex += 1) {
    if (
      summarySplittedArray[jIndex].type === 'WhiteSpace' &&
      summarySplittedArray[jIndex].raw.includes('\n')
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Handles paragraph breaks on summarizer output text.
 * @param summary output summary text
 * @returns summary text with paragraph breaks
 */
export const addParagraphBreaks = (summary: string) => {
  let summarySplittedArray = splitIntoSentences(filterOutputSummary(summary));
  let sentenceIndex = 0;

  sentenceIndex = 0;

  summarySplittedArray = summarySplittedArray.map((summaryObj, index) => {
    if (summaryObj.type === 'Sentence') sentenceIndex += 1;

    if (summaryObj.type === 'WhiteSpace' && summaryObj.raw.includes('\n')) sentenceIndex = 0;
    if (
      sentenceIndex >= 5 &&
      summaryObj.type === 'WhiteSpace' &&
      noParaAhead(summarySplittedArray, index)
    ) {
      summaryObj.raw = '\n\n';
      sentenceIndex = 0;
      return summaryObj;
    }
    return summaryObj;
  });

  return summarySplittedArray.map((summaryText) => summaryText.raw).join('');
};
